"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/authStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CartSheet } from "@/components/customer/CartSheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { isCustomerDashboardRole } from "@/lib/auth-redirect";
import { LogOut, LayoutDashboard, Calendar, ShoppingCart } from "lucide-react";
import { useRouter } from "next/navigation";

function loginHrefForReturn(returnPath: string) {
  if (returnPath === "/login" || returnPath === "/signup") return "/login";
  const params = new URLSearchParams();
  params.set("returnUrl", returnPath);
  return `/login?${params.toString()}`;
}

export function Navbar() {
  const { user, logout } = useAuthStore();
  const router = useRouter();
  const pathname = usePathname();
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [cartSheetOpen, setCartSheetOpen] = useState(false);

  const goToLogin = useCallback(
    (returnPath?: string) => {
      const target = returnPath ?? pathname;
      router.push(loginHrefForReturn(target));
    },
    [pathname, router]
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ returnUrl?: string }>).detail;
      goToLogin(detail?.returnUrl);
    };
    window.addEventListener("auth-required", handler);
    return () => window.removeEventListener("auth-required", handler);
  }, [goToLogin]);

  useEffect(() => {
    const handler = () => setCartSheetOpen(true);
    window.addEventListener("open-cart-sheet", handler);
    return () => window.removeEventListener("open-cart-sheet", handler);
  }, []);

  const handleLogout = async () => {
    setIsLogoutOpen(false);
    await logout();
    router.push("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-sm font-bold text-primary-foreground">
              VC
            </span>
          </div>
          <span className="font-bold text-foreground hidden sm:inline">
            VendorCall
          </span>
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              {(user.role === "customer" || user.role === "property_manager") && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-10 w-10 rounded-full ${
                          cartSheetOpen ? "text-muted-foreground" : "text-foreground"
                        }`}
                        aria-label="My Cart"
                        onClick={() => setCartSheetOpen(true)}
                      >
                        <ShoppingCart className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">My Cart</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link href="/customer/bookings" aria-label="My Bookings">
                        <Button variant="ghost" size="icon" className="text-foreground h-10 w-10 rounded-full">
                          <Calendar className="h-5 w-5" />
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">My Bookings</TooltipContent>
                  </Tooltip>
                </>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={user.avatarUrl || ""}
                        alt={user.firstName}
                      />
                      <AvatarFallback>
                        {user.firstName?.charAt(0)}
                        {user.lastName?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      router.push(
                        isCustomerDashboardRole(user.role)
                          ? "/customer/dashboard"
                          : `/${user.role === "property_manager" || user.role === "property-manager" ? "manager" : user.role}/dashboard`,
                      )
                    }
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    <span>Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setIsLogoutOpen(true)} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <AlertDialog open={isLogoutOpen} onOpenChange={setIsLogoutOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-sm font-semibold">
                      Sign out?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      You will be signed out. You can sign in again anytime.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleLogout}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90 cursor-pointer"
                    >
                      Sign out
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <>
              <Link href="/vendor-register">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer">
                  Join As A Vendor
                </Button>
              </Link>
              <Button variant="ghost" className="text-foreground cursor-pointer" asChild>
                <Link href={loginHrefForReturn(pathname)}>Login / Sign Up</Link>
              </Button>
            </>
          )}
        </div>
      </div>
      <CartSheet open={cartSheetOpen} onOpenChange={setCartSheetOpen} />
    </header>
  );
}